import { DateTime } from "luxon";
import { parseIsoCached, TIMESTAMP_CACHE_MAX } from "@/lib/utils";
import {
  type AttentionContent,
  type AttentionMessage,
  type AttentionQuote,
  type AttentionSendState,
  getAttentionGroupingInput,
} from "./attention-message";

const DEFAULT_GROUPING_WINDOW_MINUTES = 5;

// Domain model doesn't carry reactions; the live channel hook injects them via
// BuildAttentionViewOptions (fixtures can omit them).
export interface AttentionReaction {
  emoji: string;
  count: number;
  reactedBySelf: boolean;
  /** Sendbird emoji PNG url; `null` (fixtures/unknown key) falls back to the key text. */
  url: string | null;
}

export type AttentionBubbleContent = Extract<
  AttentionContent,
  {
    type:
      | "text"
      | "supervision"
      | "gif"
      | "media"
      | "entityInteraction"
      | "hidden"
      | "unsupported";
  }
>;

export type AttentionCenteredContent = Extract<
  AttentionContent,
  { type: "system" | "activity" | "entityShared" | "game" | "takedown" }
>;

export type AttentionBubbleItem = AttentionBubbleContent & {
  id: string;
  quote: AttentionQuote | null;
  reactions: AttentionReaction[];
  sendState: AttentionSendState;
};

export type AttentionGroupDirection = "incoming" | "outgoing";

export type AttentionRow =
  | {
      type: "group";
      key: string;
      direction: AttentionGroupDirection;
      senderId: string | null;
      senderLabel: string | null;
      senderAvatarUrl: string | null;
      /** Group conversations label incoming runs with the sender; 1:1 threads never do. */
      showSenderHeader: boolean;
      items: AttentionBubbleItem[];
      timestampLabel: string;
      showReadReceipt: boolean;
    }
  | {
      type: "centered";
      key: string;
      messageId: string;
      centered: AttentionCenteredContent;
      timestampLabel: string;
    }
  | {
      type: "dateSeparator";
      key: string;
      label: string;
    };

export interface AttentionView {
  rows: AttentionRow[];
}

export interface BuildAttentionViewOptions {
  groupingWindowMinutes?: number;
  reactionsByMessageId?: Record<string, AttentionReaction[]>;
  /** Multi-party thread: incoming runs carry a per-sender name/avatar header. */
  isGroup?: boolean;
}

/** Placement only — does not clone or reshape the content payload. */
export function contentPlacement(
  content: AttentionContent,
): "bubble" | "centered" {
  switch (content.type) {
    case "text":
    case "supervision":
    case "gif":
    case "media":
    case "entityInteraction":
    case "hidden":
    case "unsupported":
      return "bubble";
    case "system":
    case "activity":
    case "entityShared":
    case "game":
    case "takedown":
      return "centered";
    default: {
      const exhaustive: never = content;
      return exhaustive;
    }
  }
}

const timestampLabelCache = new Map<string, string>();
function formatTimestampLabel(iso: string): string {
  let label = timestampLabelCache.get(iso);
  if (label === undefined) {
    const parsed = parseIsoCached(iso);
    label = parsed.isValid ? parsed.toFormat("t") : "";
    if (timestampLabelCache.size >= TIMESTAMP_CACHE_MAX)
      timestampLabelCache.clear();
    timestampLabelCache.set(iso, label);
  }
  return label;
}

// `now` is injectable so callers/tests can pin a fixed reference.
export function formatDateSeparatorLabel(
  iso: string,
  now: DateTime = DateTime.now(),
): string {
  const parsed = parseIsoCached(iso);
  if (!parsed.isValid) return "";
  const day = parsed.startOf("day");
  const today = now.startOf("day");
  const diffDays = Math.round(today.diff(day, "days").days);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return day.year === today.year
    ? day.toFormat("LLLL d")
    : day.toFormat("LLLL d, yyyy");
}

/** Calendar day key in `zone` (IANA). Defaults to local for Attention. */
const dayKeyCache = new Map<string, string | null>();
export function dayKeyOf(iso: string, zone?: string): string | null {
  const cacheKey = `${zone ?? ""}|${iso}`;
  const cached = dayKeyCache.get(cacheKey);
  if (cached !== undefined) return cached;
  const key = computeDayKey(iso, zone);
  if (dayKeyCache.size >= TIMESTAMP_CACHE_MAX) dayKeyCache.clear();
  dayKeyCache.set(cacheKey, key);
  return key;
}

function computeDayKey(iso: string, zone?: string): string | null {
  const resolve = (dt: DateTime): string | null => {
    if (!dt.isValid) return null;
    return (zone ? dt.setZone(zone) : dt).startOf("day").toISODate();
  };

  const fromIso = resolve(parseIsoCached(iso));
  if (fromIso !== null) return fromIso;

  // Postgres SQL timestamps (`YYYY-MM-DD HH:mm:ss.SSS+00`) are not ISO.
  const normalized = iso.replace(" ", "T").replace(/([+-]\d{2})$/, "$1:00");
  const retried = resolve(DateTime.fromISO(normalized));
  if (retried !== null) return retried;

  return resolve(DateTime.fromSQL(iso));
}

/** Civil YYYY-MM-DD from a calendar cell Date (ignores timezone of the instant). */
export function civilDayKeyOf(date: Date): string {
  return DateTime.fromObject({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  }).toISODate()!;
}

function senderLabelOf(message: AttentionMessage): string | null {
  return message.sender?.displayName ?? message.sender?.nickname ?? null;
}

interface GroupAccumulator {
  key: string;
  direction: AttentionGroupDirection;
  senderId: string | null;
  senderLabel: string | null;
  senderAvatarUrl: string | null;
  items: AttentionBubbleItem[];
  lastSentAtMs: number;
  lastCreatedAt: string;
  hasRead: boolean;
}

/**
 * Consecutive same-sender/direction messages within a 5-minute window collapse
 * into one group; a quote, direction change, or centered message breaks it.
 * The read icon stays on the latest outgoing group the teen has read,
 * including when a newer outgoing message is still unread.
 */
export function buildAttentionView(
  messages: AttentionMessage[],
  options: BuildAttentionViewOptions = {},
): AttentionView {
  const windowMs =
    (options.groupingWindowMinutes ?? DEFAULT_GROUPING_WINDOW_MINUTES) *
    60 *
    1000;
  const reactionsByMessageId = options.reactionsByMessageId ?? {};
  const isGroup = options.isGroup ?? false;

  const rows: AttentionRow[] = [];
  let current: GroupAccumulator | null = null;
  let previousDayKey: string | null = null;
  let lastReadGroupKey: string | null = null;

  const flush = (): void => {
    if (current === null) return;
    if (current.direction === "outgoing" && current.hasRead) {
      lastReadGroupKey = current.key;
    }
    rows.push({
      type: "group",
      key: current.key,
      direction: current.direction,
      senderId: current.senderId,
      senderLabel: current.senderLabel,
      senderAvatarUrl: current.senderAvatarUrl,
      showSenderHeader: isGroup && current.direction === "incoming",
      items: current.items,
      timestampLabel: formatTimestampLabel(current.lastCreatedAt),
      showReadReceipt: false,
    });
    current = null;
  };

  for (const message of messages) {
    const dayKey = dayKeyOf(message.createdAt);
    if (dayKey !== null && dayKey !== previousDayKey) {
      flush();
      rows.push({
        type: "dateSeparator",
        key: `date-${dayKey}`,
        label: formatDateSeparatorLabel(message.createdAt),
      });
      previousDayKey = dayKey;
    }

    if (contentPlacement(message.content) === "centered") {
      flush();
      rows.push({
        type: "centered",
        key: `centered-${message.id}`,
        messageId: message.id,
        centered: message.content as AttentionCenteredContent,
        timestampLabel: formatTimestampLabel(message.createdAt),
      });
      continue;
    }

    const grouping = getAttentionGroupingInput(message);
    const direction: AttentionGroupDirection =
      message.direction === "outgoing" ? "outgoing" : "incoming";
    const item: AttentionBubbleItem = {
      ...(message.content as AttentionBubbleContent),
      id: message.id,
      quote: message.quote,
      reactions: reactionsByMessageId[message.id] ?? [],
      sendState: message.sendState,
    };

    const canAppend =
      current !== null &&
      message.quote === null &&
      current.direction === direction &&
      current.senderId === grouping.senderId &&
      grouping.sentAtMs - current.lastSentAtMs <= windowMs &&
      (direction !== "outgoing" ||
        (message.readState === "read") === current.hasRead);

    if (canAppend && current !== null) {
      current.items.push(item);
      current.lastSentAtMs = grouping.sentAtMs;
      current.lastCreatedAt = message.createdAt;
      if (message.readState === "read") current.hasRead = true;
    } else {
      flush();
      current = {
        key: `group-${message.id}`,
        direction,
        senderId: grouping.senderId,
        senderLabel: senderLabelOf(message),
        senderAvatarUrl: message.sender?.avatarUrl ?? null,
        items: [item],
        lastSentAtMs: grouping.sentAtMs,
        lastCreatedAt: message.createdAt,
        hasRead: message.readState === "read",
      };
    }
  }

  flush();

  if (lastReadGroupKey !== null) {
    const readRow = rows.find(
      (row) => row.type === "group" && row.key === lastReadGroupKey,
    );
    if (readRow?.type === "group") readRow.showReadReceipt = true;
  }

  return { rows };
}
